'use client'

import { useState } from 'react'
import Link from 'next/link'
import { PublicNav } from '../components/public-nav'
import { PublicFooter } from '../components/public-footer'
import { lp } from '../_home/locale-path'
import { Mail, MessageCircle, Phone, BookOpen, ArrowRight, Send, PlayCircle, Sparkles } from 'lucide-react'

type Lang = 'de' | 'en' | 'tr'

const C = {
  de: {
    heroEyebrow: '— Service & Support',
    heroTitle1: 'Wir sind nur einen',
    heroTitle2: 'Anruf entfernt.',
    heroSub: 'Echte Menschen, schnelle Antworten — Pro-Kunden in unter 2 Stunden, Enterprise unter 30 Minuten.',
    cardEmailTitle: 'E-Mail',
    cardEmailDesc: 'Wir antworten werktags innerhalb weniger Stunden.',
    cardPhoneTitle: 'Telefon',
    cardPhoneDesc: 'Mo–Fr 09:00–18:00 Uhr · Pro & Enterprise.',
    cardChatTitle: 'Live-Chat',
    cardChatDesc: 'Direkt im Admin-Panel — schneller Antwort-Bot + Mensch.',
    cardChatCta: 'Jetzt starten',
    demoEyebrow: '— Demo',
    demoTitle1: 'Sehen Sie es',
    demoTitle2: 'in Aktion.',
    demoIntro: '20 Minuten Live-Demo per Video-Call. Wir zeigen Ihnen Rezaro an einem realen Beispiel — Ihrem Restaurant. Keine Verkaufsmasche, nur Antworten auf Ihre Fragen.',
    demoCardTitle: 'Live-Demo (20 min)',
    demoCardSub: 'Mo–Fr · Termine im Halbstunden-Takt',
    demoBullets: [
      'Reservierungen + Bestellungen + Speisekarte live',
      'Persönliche Antworten auf Ihre Fragen',
      'Konkretes Setup für Ihren Betrieb',
    ],
    helpTitle: 'Lieber selbst lesen?',
    helpDesc: 'Im Hilfe-Center finden Sie über 25 Schritt-für-Schritt-Anleitungen.',
    helpLink: 'Zum Hilfe-Center →',
    formTitle: 'Demo anfragen',
    formSub: 'Wir melden uns innerhalb eines Werktages.',
    sentTitle: 'Anfrage erhalten!',
    sentPre: 'Wir melden uns innerhalb eines Werktages bei ',
    sentPost: '.',
    labelName: 'Name',
    phName: 'Vor- und Nachname',
    labelEmail: 'E-Mail',
    phEmail: 'ihre@email.de',
    labelRestaurant: 'Restaurant',
    phRestaurant: 'Name & Stadt',
    labelMessage: 'Ihre Frage',
    phMessage: 'Was möchten Sie wissen?',
    sending: 'Wird gesendet…',
    submit: 'Anfrage senden',
    consentPre: 'Mit dem Absenden akzeptieren Sie unsere',
    consentLink: 'Datenschutzerklärung',
    consentPost: '.',
    errSend: 'Senden fehlgeschlagen, bitte erneut versuchen.',
    errNetwork: 'Netzwerkfehler. Bitte erneut versuchen.',
    faqEyebrow: '— FAQ',
    faqTitle: 'Häufig gestellte Fragen.',
    faqAll: 'Alle FAQs ansehen',
    faqs: [
      { q: 'Wie lange dauert die Einrichtung?', a: 'Unter 10 Minuten — unser Assistent führt Sie durch jeden Schritt: Öffnungszeiten, Tische, Speisekarte, Widget einbinden.' },
      { q: 'Brauche ich technische Vorkenntnisse?', a: 'Nein. Rezaro ist für Wirtinnen und Wirte gemacht. Wenn Sie WhatsApp bedienen können, können Sie Rezaro bedienen.' },
      { q: 'Welche Kanäle bietet der Support?', a: 'E-Mail, Live-Chat in der App und Telefon. Pro-Kunden: < 2h Antwortzeit. Enterprise: dedizierter Account-Manager.' },
      { q: 'Welche Sprachen werden unterstützt?', a: 'Deutsch, Englisch, Italienisch, Französisch, Türkisch — sowohl im Admin als auch für Gäste.' },
      { q: 'Ist Rezaro DSGVO-konform?', a: 'Ja. Server in Frankfurt am Main, ISO-27001-zertifiziert. Auftragsverarbeitungsvertrag (AVV) auf Anfrage.' },
    ],
  },
  en: {
    heroEyebrow: '— Service & Support',
    heroTitle1: 'We are just one',
    heroTitle2: 'call away.',
    heroSub: 'Real people, fast answers — Pro customers in under 2 hours, Enterprise in under 30 minutes.',
    cardEmailTitle: 'Email',
    cardEmailDesc: 'We reply within a few hours on business days.',
    cardPhoneTitle: 'Phone',
    cardPhoneDesc: 'Mon–Fri 9:00–18:00 · Pro & Enterprise.',
    cardChatTitle: 'Live chat',
    cardChatDesc: 'Right inside the admin panel — fast answer bot + human.',
    cardChatCta: 'Get started',
    demoEyebrow: '— Demo',
    demoTitle1: 'See it',
    demoTitle2: 'in action.',
    demoIntro: 'A 20-minute live demo over video call. We show you Rezaro using a real example — your restaurant. No sales pitch, just answers to your questions.',
    demoCardTitle: 'Live demo (20 min)',
    demoCardSub: 'Mon–Fri · slots every half hour',
    demoBullets: [
      'Reservations + orders + menu, live',
      'Personal answers to your questions',
      'A concrete setup for your business',
    ],
    helpTitle: 'Prefer to read it yourself?',
    helpDesc: 'The Help Center has over 25 step-by-step guides.',
    helpLink: 'To the Help Center →',
    formTitle: 'Request a demo',
    formSub: 'We get back to you within one business day.',
    sentTitle: 'Request received!',
    sentPre: 'We will get back to you within one business day at ',
    sentPost: '.',
    labelName: 'Name',
    phName: 'First and last name',
    labelEmail: 'Email',
    phEmail: 'you@email.com',
    labelRestaurant: 'Restaurant',
    phRestaurant: 'Name & city',
    labelMessage: 'Your question',
    phMessage: 'What would you like to know?',
    sending: 'Sending…',
    submit: 'Send request',
    consentPre: 'By submitting you accept our',
    consentLink: 'Privacy Policy',
    consentPost: '.',
    errSend: 'Sending failed, please try again.',
    errNetwork: 'Network error. Please try again.',
    faqEyebrow: '— FAQ',
    faqTitle: 'Frequently asked questions.',
    faqAll: 'View all FAQs',
    faqs: [
      { q: 'How long does setup take?', a: 'Under 10 minutes — our assistant walks you through every step: opening hours, tables, menu, embedding the widget.' },
      { q: 'Do I need any technical knowledge?', a: 'No. Rezaro is built for restaurateurs. If you can use WhatsApp, you can use Rezaro.' },
      { q: 'Which support channels do you offer?', a: 'Email, in-app live chat and phone. Pro customers: < 2h response time. Enterprise: dedicated account manager.' },
      { q: 'Which languages are supported?', a: 'German, English, Italian, French, Turkish — both in the admin and for guests.' },
      { q: 'Is Rezaro GDPR-compliant?', a: 'Yes. Servers in Frankfurt am Main, ISO 27001 certified. Data processing agreement (DPA) on request.' },
    ],
  },
  tr: {
    heroEyebrow: '— Servis & Destek',
    heroTitle1: 'Bize ulaşmanız',
    heroTitle2: 'sadece bir telefon.',
    heroSub: 'Gerçek insanlar, hızlı yanıtlar — Pro müşteriler için 2 saatin altında, Enterprise için 30 dakikanın altında.',
    cardEmailTitle: 'E-posta',
    cardEmailDesc: 'İş günlerinde birkaç saat içinde yanıt veriyoruz.',
    cardPhoneTitle: 'Telefon',
    cardPhoneDesc: 'Pzt–Cum 09:00–18:00 · Pro & Enterprise.',
    cardChatTitle: 'Canlı sohbet',
    cardChatDesc: 'Doğrudan yönetim panelinde — hızlı yanıt botu + gerçek kişi.',
    cardChatCta: 'Hemen başlayın',
    demoEyebrow: '— Demo',
    demoTitle1: 'Onu',
    demoTitle2: 'iş başında görün.',
    demoIntro: 'Görüntülü görüşmede 20 dakikalık canlı demo. Rezaro’yu gerçek bir örnekle — sizin restoranınızla — gösteriyoruz. Satış baskısı yok, yalnızca sorularınıza yanıtlar.',
    demoCardTitle: 'Canlı demo (20 dk)',
    demoCardSub: 'Pzt–Cum · yarım saatte bir randevu',
    demoBullets: [
      'Rezervasyonlar + siparişler + menü canlı',
      'Sorularınıza kişisel yanıtlar',
      'İşletmeniz için somut kurulum',
    ],
    helpTitle: 'Kendiniz okumayı mı tercih edersiniz?',
    helpDesc: 'Yardım Merkezi’nde 25’ten fazla adım adım rehber bulabilirsiniz.',
    helpLink: 'Yardım Merkezi’ne →',
    formTitle: 'Demo talep edin',
    formSub: 'Bir iş günü içinde size geri döneriz.',
    sentTitle: 'Talebiniz alındı!',
    sentPre: 'Bir iş günü içinde şu adresten size geri döneceğiz: ',
    sentPost: '.',
    labelName: 'Ad',
    phName: 'Ad ve soyad',
    labelEmail: 'E-posta',
    phEmail: 'siz@email.com',
    labelRestaurant: 'Restoran',
    phRestaurant: 'Ad & şehir',
    labelMessage: 'Sorunuz',
    phMessage: 'Ne öğrenmek istersiniz?',
    sending: 'Gönderiliyor…',
    submit: 'Talebi gönder',
    consentPre: 'Göndererek',
    consentLink: 'Gizlilik Politikamızı',
    consentPost: ' kabul edersiniz.',
    errSend: 'Gönderim başarısız oldu, lütfen tekrar deneyin.',
    errNetwork: 'Ağ hatası. Lütfen tekrar deneyin.',
    faqEyebrow: '— SSS',
    faqTitle: 'Sıkça sorulan sorular.',
    faqAll: 'Tüm SSS’leri görüntüle',
    faqs: [
      { q: 'Kurulum ne kadar sürer?', a: '10 dakikanın altında — asistanımız her adımda size yol gösterir: çalışma saatleri, masalar, menü, widget yerleştirme.' },
      { q: 'Teknik bilgiye ihtiyacım var mı?', a: 'Hayır. Rezaro restoran işletmecileri için tasarlandı. WhatsApp kullanabiliyorsanız Rezaro’yu da kullanabilirsiniz.' },
      { q: 'Destek hangi kanalları sunuyor?', a: 'E-posta, uygulama içi canlı sohbet ve telefon. Pro müşteriler: < 2 saat yanıt süresi. Enterprise: özel hesap yöneticisi.' },
      { q: 'Hangi diller destekleniyor?', a: 'Almanca, İngilizce, İtalyanca, Fransızca, Türkçe — hem yönetimde hem de misafirler için.' },
      { q: 'Rezaro GDPR uyumlu mu?', a: 'Evet. Sunucular Frankfurt am Main’de, ISO 27001 sertifikalı. Veri işleme sözleşmesi (AVV) talep üzerine.' },
    ],
  },
} as const

export function ServiceView({ lang = 'de' }: { lang?: Lang }) {
  const t = C[lang]
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [restaurant, setRestaurant] = useState('')
  const [message, setMessage] = useState('')
  const [sent, setSent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!email.trim() || !message.trim()) return
    setSubmitting(true)
    try {
      const res = await fetch('/api/support/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim() || null,
          email: email.trim(),
          restaurant: restaurant.trim() || null,
          message: message.trim(),
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setError(data.error || t.errSend)
        return
      }
      setSent(true)
    } catch {
      setError(t.errNetwork)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="bg-pub-bg" style={{ fontFamily: 'var(--font-pub)' }}>
      <style>{`
        .display-serif { font-family: var(--font-display); font-style: italic; font-weight: 400; }
        .gradient-text {
          color: #fcd9a8;
          font-family: var(--font-display);
          font-weight: 500;
          font-style: italic;
          letter-spacing: -0.02em;
        }
        .mesh-bg {
          background-image:
            radial-gradient(at 18% 0%, rgba(245, 230, 211, 0.10) 0px, transparent 55%),
            radial-gradient(at 82% 0%, rgba(194, 65, 12, 0.10) 0px, transparent 55%);
        }
      `}</style>

      <PublicNav lang={lang} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-pub-dark text-white">
        <div className="absolute inset-0 mesh-bg opacity-90" />
        <div className="relative mx-auto max-w-4xl px-6 pt-24 pb-16 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pub-accent mb-4">{t.heroEyebrow}</p>
          <h1 className="font-semibold leading-[0.95] tracking-tight"
            style={{ fontSize: 'clamp(2.6rem, 5vw, 4.5rem)' }}>
            {t.heroTitle1}<br />
            <span className="display-serif gradient-text">{t.heroTitle2}</span>
          </h1>
          <p className="mt-7 max-w-xl mx-auto text-[16px] text-white/65 leading-relaxed">
            {t.heroSub}
          </p>
        </div>
      </section>

      {/* CONTACT CARDS */}
      <section className="bg-pub-bg">
        <div className="mx-auto max-w-6xl px-6 -mt-12 pb-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                icon: Mail,
                title: t.cardEmailTitle,
                desc: t.cardEmailDesc,
                href: 'mailto:support@rezaro.de',
                cta: 'support@rezaro.de',
                color: '#1a3a2e',
              },
              {
                icon: Phone,
                title: t.cardPhoneTitle,
                desc: t.cardPhoneDesc,
                href: 'tel:+493012345678',
                cta: '+49 30 12345678',
                color: '#1a3a2e',
              },
              {
                icon: MessageCircle,
                title: t.cardChatTitle,
                desc: t.cardChatDesc,
                href: lp('/signup', lang),
                cta: t.cardChatCta,
                color: '#10B981',
              },
            ].map(c => (
              <a key={c.title} href={c.href}
                className="group rounded-2xl border border-pub-rule bg-pub-surface p-7 transition-all hover:border-pub-accent/40 hover:shadow-xl hover:-translate-y-0.5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl mb-4"
                  style={{ background: c.color + '15' }}>
                  <c.icon className="h-5 w-5" style={{ color: c.color }} strokeWidth={1.6} />
                </div>
                <p className="text-[16px] font-semibold text-pub-ink mb-1">{c.title}</p>
                <p className="text-[13.5px] text-pub-muted leading-relaxed mb-3">{c.desc}</p>
                <p className="text-[13px] font-semibold flex items-center gap-1.5" style={{ color: c.color }}>
                  {c.cta} <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* DEMO + FORM */}
      <section className="border-y border-pub-rule bg-pub-surface">
        <div className="mx-auto max-w-6xl px-6 py-24">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            {/* Left: demo CTA */}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pub-accent mb-4">{t.demoEyebrow}</p>
              <h2 className="text-pub-ink font-semibold leading-[1.05] tracking-tight"
                style={{ fontSize: 'clamp(2rem, 3.5vw, 2.8rem)' }}>
                {t.demoTitle1}<br />
                <span className="display-serif gradient-text">{t.demoTitle2}</span>
              </h2>
              <p className="mt-6 text-[15.5px] leading-relaxed text-pub-muted max-w-md">
                {t.demoIntro}
              </p>

              <div className="mt-8 rounded-2xl border border-pub-rule bg-pub-bg p-6 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-pub-accent text-white">
                    <PlayCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-[14px] font-semibold text-pub-ink">{t.demoCardTitle}</p>
                    <p className="text-[12px] text-pub-muted">{t.demoCardSub}</p>
                  </div>
                </div>
                <ul className="space-y-1.5 text-[13px] text-pub-ink pt-1">
                  {t.demoBullets.map(tx => (
                    <li key={tx} className="flex items-start gap-2">
                      <Sparkles className="h-3.5 w-3.5 text-pub-accent mt-0.5 flex-shrink-0" />
                      {tx}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 rounded-2xl border border-pub-rule bg-pub-bg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <BookOpen className="h-5 w-5 text-pub-accent" />
                  <p className="text-[14px] font-semibold text-pub-ink">{t.helpTitle}</p>
                </div>
                <p className="text-[13px] text-pub-muted mb-3">
                  {t.helpDesc}
                </p>
                <Link href="/hilfe" className="text-[13px] font-semibold text-pub-accent hover:underline">
                  {t.helpLink}
                </Link>
              </div>
            </div>

            {/* Right: form */}
            <div className="rounded-2xl border border-pub-rule bg-pub-bg p-8 shadow-xl">
              <h3 className="text-[20px] font-semibold text-pub-ink mb-1">{t.formTitle}</h3>
              <p className="text-[13px] text-pub-muted mb-6">{t.formSub}</p>

              {sent ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 mb-4">
                    <Send className="h-6 w-6 text-emerald-600" />
                  </div>
                  <p className="text-[16px] font-semibold text-pub-ink">{t.sentTitle}</p>
                  <p className="text-[13px] text-pub-muted mt-1 max-w-xs">
                    {t.sentPre}<strong>{email}</strong>{t.sentPost}
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  <div>
                    <label className="text-[12px] font-medium text-pub-ink">{t.labelName}</label>
                    <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
                      placeholder={t.phName}
                      className="mt-1 h-11 w-full rounded-lg border border-pub-rule bg-pub-surface px-3.5 text-[14px] text-pub-ink placeholder:text-pub-muted focus:outline-none focus:ring-2 focus:ring-pub-accent/30 focus:border-pub-accent/50" />
                  </div>
                  <div>
                    <label className="text-[12px] font-medium text-pub-ink">{t.labelEmail}</label>
                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder={t.phEmail}
                      className="mt-1 h-11 w-full rounded-lg border border-pub-rule bg-pub-surface px-3.5 text-[14px] text-pub-ink placeholder:text-pub-muted focus:outline-none focus:ring-2 focus:ring-pub-accent/30 focus:border-pub-accent/50" />
                  </div>
                  <div>
                    <label className="text-[12px] font-medium text-pub-ink">{t.labelRestaurant}</label>
                    <input type="text" value={restaurant} onChange={(e) => setRestaurant(e.target.value)}
                      placeholder={t.phRestaurant}
                      className="mt-1 h-11 w-full rounded-lg border border-pub-rule bg-pub-surface px-3.5 text-[14px] text-pub-ink placeholder:text-pub-muted focus:outline-none focus:ring-2 focus:ring-pub-accent/30 focus:border-pub-accent/50" />
                  </div>
                  <div>
                    <label className="text-[12px] font-medium text-pub-ink">{t.labelMessage}</label>
                    <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
                      placeholder={t.phMessage}
                      className="mt-1 w-full rounded-lg border border-pub-rule bg-pub-surface p-3 text-[14px] text-pub-ink placeholder:text-pub-muted focus:outline-none focus:ring-2 focus:ring-pub-accent/30 focus:border-pub-accent/50" />
                  </div>
                  <button type="submit" disabled={submitting}
                    className="w-full rounded-full bg-pub-accent py-3 text-[14px] font-semibold text-white hover:bg-pub-accent-dark transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                    {submitting ? t.sending : <>{t.submit} <Send className="h-4 w-4" /></>}
                  </button>
                  {error && <p className="text-[12px] text-red-600 text-center">{error}</p>}
                  <p className="text-[11px] text-pub-muted text-center">
                    {t.consentPre}{' '}
                    <Link href="/datenschutz" className="underline">{t.consentLink}</Link>{t.consentPost}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-pub-bg">
        <div className="mx-auto max-w-3xl px-6 py-20">
          <div className="text-center mb-12">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pub-accent mb-3">{t.faqEyebrow}</p>
            <h2 className="font-semibold tracking-tight text-pub-ink"
              style={{ fontSize: 'clamp(1.8rem, 3vw, 2.6rem)' }}>
              {t.faqTitle}
            </h2>
          </div>
          <div className="space-y-3">
            {t.faqs.map((f, i) => (
              <details key={i} className="group rounded-2xl border border-pub-rule bg-pub-surface p-6 transition-colors open:border-pub-accent">
                <summary className="flex cursor-pointer items-center justify-between gap-4 font-medium text-pub-ink text-[15px]">
                  {f.q}
                  <span className="text-pub-accent transition-transform group-open:rotate-45 text-3xl leading-none">+</span>
                </summary>
                <p className="mt-4 text-[14px] leading-relaxed text-pub-muted">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link href="/faq" className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-pub-accent hover:gap-2.5 transition-all">
              {t.faqAll} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter lang={lang} />
    </div>
  )
}
